import { toast } from "sonner";
import { t } from "i18next";

// Common handling for the top-right error toast.
//
// Backend messages fall into two very different groups, and we used to show both verbatim:
//
//  * A service throwing a domain error -- `BadRequestException("Email already in use")` -- is
//    written for the person who triggered the action. Worth showing.
//  * Everything else -- DTO validation ("videoState must be an array"), 5xx, network failures --
//    is internal detail. It means nothing to a customer and reads as the product being broken.
//
// The two are distinguishable: NestJS's global ValidationPipe puts an array of messages on the
// body, domain exceptions put a plain string. So field validation is always treated as internal.
//
// SuperAdmins see the raw message either way, plus which request produced it.
let isSuperAdmin = false;

export const setErrorToastSuperAdmin = (value: boolean) => {
  isSuperAdmin = value;
};

export type ErrorToast = { title: string; description?: string };

// A rejected DTO carries one message per offending field. Run together on a single line they're
// unreadable, so give each its own line -- bulleted once there's more than one to tell apart.
// Only SuperAdmins ever see these, and the toast title renders with `whitespace-pre-line`.
const joinFieldMessages = (messages: unknown[]) => {
  const lines = messages.filter(Boolean).map(String);
  return lines.length > 1
    ? lines.map((line) => `• ${line}`).join("\n")
    : lines.join("\n");
};

// The edge agent reports a failure as a chain of causes, outermost first, joined by " > ":
//
//   failed to capture image > failed reconnecting after failed capture >
//   Error reconnecting to video stream > Error opening file: v4l2src device=/dev/video10 ! ...
//
// Only the first link is about what the user asked for; the rest is agent and gstreamer internals
// that the backend passes through verbatim (DeviceRequestError -> body.message, usually a 422, so
// it reads as a client error and would otherwise be shown in full). Users get the first link,
// SuperAdmins get the whole chain, one cause per line.
const CHAIN_SEPARATOR = " > ";

const splitCauseChain = (message: string) =>
  message
    .split(CHAIN_SEPARATOR)
    .map((cause) => cause.trim())
    .filter(Boolean);

const parse = (error: any) => {
  const raw = error?.body?.message;
  const isFieldValidation = Array.isArray(raw);
  const text = isFieldValidation ? joinFieldMessages(raw) : raw;
  const message =
    typeof text === "string" && text.trim() !== "" ? text : undefined;
  // Field validation lists independent failures, not a chain -- never split those.
  const causes = message && !isFieldValidation ? splitCauseChain(message) : [];
  const status = typeof error?.status === "number" ? error.status : undefined;
  // ApiError keeps the whole ApiRequestOptions, so the verb is available -- worth showing, since
  // one path serves several of them. AxiosError spells it lowercase on `config` instead.
  const method: string | undefined =
    error?.request?.method ?? error?.config?.method?.toUpperCase();
  const details = [status, [method, error?.url].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(" — ");
  return {
    // What the user asked for, without the internals behind it.
    summary: causes[0] ?? message,
    // Everything, indented past the first line so the chain reads top-down.
    full:
      causes.length > 1
        ? causes.map((c, i) => (i === 0 ? c : `→ ${c}`)).join("\n")
        : message,
    isFieldValidation,
    status,
    details: details === "" ? undefined : details,
    // Only a 4xx says "the request itself was wrong"; a 5xx, a network failure or an aborted
    // request says nothing the user can act on.
    isClientError: status !== undefined && status >= 400 && status < 500,
  };
};

// The interceptor in client/index.ts already navigates on these -- to /login, or to the
// unauthorized page. A toast on top of a page change is noise.
const isHandledByNavigation = (status?: number) =>
  status === 401 || status === 403 || status === 440;

export const resolveRequestErrorToast = (
  error: unknown,
  opts: { isSuperAdmin: boolean },
): ErrorToast | null => {
  // Failed data fetches. The page already shows its own empty state, so a customer gets nothing --
  // a raw message next to "No results found." only makes an empty table look broken.
  if (!opts.isSuperAdmin) return null;
  const { full, details } = parse(error);
  return full ? { title: full, description: details } : null;
};

export const resolveActionErrorToast = (
  error: unknown,
  opts: { isSuperAdmin: boolean; fallback: string },
): ErrorToast | null => {
  const { summary, full, isFieldValidation, status, details, isClientError } =
    parse(error);

  if (opts.isSuperAdmin) {
    return { title: full ?? opts.fallback, description: details };
  }
  if (isHandledByNavigation(status)) return null;

  // Unlike a fetch there is no empty state to fall back on, so a failed action always says
  // *something* -- `fallback` when there's nothing safe to quote.
  const isUserFacing = isClientError && !isFieldValidation && summary;
  return { title: isUserFacing ? summary : opts.fallback };
};

const show = (result: ErrorToast | null) => {
  if (!result) return;
  toast.error(result.title, {
    description: result.description,
    // Field lists and agent cause chains are newline-separated, and HTML collapses those by
    // default. Set per toast rather than on the Toaster: sonner merges the two independently, and
    // the Toaster's own toastOptions are currently overwritten by each layout (see sonner.tsx).
    classNames: { title: "whitespace-pre-line" },
  });
};

// Failed data fetches (the query cache's onError).
export const showRequestErrorToast = (error: unknown) =>
  show(resolveRequestErrorToast(error, { isSuperAdmin }));

// Translations arrive over http, so a request that fails early enough would get the bare key back
// -- `defaultValue` keeps the last-resort message readable in that window.
const defaultFallback = () =>
  t("actionFailed", {
    defaultValue: "Something went wrong. Please try again.",
  });

// Failed user-triggered actions (mutations, and the handful of manual try/catch equivalents).
export const showActionErrorToast = (error: unknown, fallback?: string) =>
  show(
    resolveActionErrorToast(error, {
      isSuperAdmin,
      fallback: fallback ?? defaultFallback(),
    }),
  );

// The same resolution as the action toast, for errors a component renders inline instead. Never
// the transport's status text (`ApiError.message` is "Unprocessable Content"), which is what a
// bare `error.message` would show.
export const actionErrorMessage = (error: unknown, fallback: string): string =>
  resolveActionErrorToast(error, { isSuperAdmin, fallback })?.title ?? fallback;
